import { PageConfigService, PRODUCTS_PER_PAGE, ProductService } from '@core';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import {
  mapProductDetailsDtoToInternal,
  PaginatedOffset,
  PaginationParams,
  ProductDetails,
  ProductDetailsDto,
  ProductSearchQueryParams,
  SortBy,
} from '@models';
import { GetProductsParams, ProductListFacadeSignals } from './product-list.model';

@Injectable({
  providedIn: 'root',
})
export class ProductsListService implements ProductListFacadeSignals {
  protected readonly productService = inject(ProductService);
  protected readonly destroyRef = inject(DestroyRef);
  protected pageConfigService = inject(PageConfigService);

  title = computed(() => '');
  subtitle = computed<string>(() => '');
  products = signal<ProductDetails[]>([]);
  isLoading = signal(false);
  initialLoading = signal(true);
  totalCount = signal(0);
  isLastPage = signal(false);
  productsPerPage = signal(PRODUCTS_PER_PAGE).asReadonly();
  protected cachedFilter = signal<GetProductsParams>({});
  filters = this.cachedFilter.asReadonly();

  getProducts(filterValue: GetProductsParams, pagination?: PaginationParams) {
    if (!Object.keys(filterValue).length) {
      return;
    }

    this.isLoading.set(true);

    // Check if filter value has changed, and reset page if so
    const isFilterChanged = this.isFilterChanged(filterValue);
    if (isFilterChanged) {
      this.cachedFilter.set(filterValue);
    }

    const params: ProductSearchQueryParams = {
      ...this.transformFilterValue(filterValue, isFilterChanged),
      ...(pagination ? pagination : {}),
    };
    return this.getList(params)
      .pipe(
        catchError(() => EMPTY),
        finalize(() => {
          this.isLoading.set(false);
          this.initialLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => this.handleResponse(response, isFilterChanged));
  }

  protected getList(params: ProductSearchQueryParams): Observable<PaginatedOffset<ProductDetailsDto>> {
    return this.productService.getProducts(params);
  }

  setInitialState(products: ProductDetails[], totalCount: number, filters: GetProductsParams): void {
    this.products.set(products);
    this.totalCount.set(totalCount);
    this.isLastPage.set(false);
    this.initialLoading.set(false);
    this.cachedFilter.set(filters);
  }

  seeMoreProducts(): void {
    this.getProducts(this.cachedFilter() || {});
  }

  protected isFilterChanged(filterValue: GetProductsParams): boolean {
    return JSON.stringify(this.cachedFilter()) !== JSON.stringify(filterValue);
  }

  protected transformFilterValue(filterValue: GetProductsParams, isFilterChanged: boolean): ProductSearchQueryParams {
    const { sortBy, ...filters } = filterValue;

    const brandIds = (Array.isArray(filters.brandIds) ? filters.brandIds : [filters.brandIds]) as number[];
    const categoryIds = this.normalizeCategoryIds(filters.category);
    const params: ProductSearchQueryParams = {
      ...filters,
      sortBy: sortBy as SortBy,
      brandIds,
      categoryIds,
      offset: isFilterChanged ? 0 : this.products().length,
      isPromo: true,
      count: this.productsPerPage(),
    };
    return params;
  }

  protected normalizeCategoryIds(category: unknown): number[] {
    if (Array.isArray(category)) {
      return category.filter((id): id is number => id != null);
    }

    return category != null ? [category as number] : [];
  }

  protected handleResponse(response: PaginatedOffset<ProductDetailsDto>, isFilterChanged: boolean): void {
    const currentItems = this.products();
    const newItems = response.items.map(mapProductDetailsDtoToInternal);

    const combinedItems = isFilterChanged || !response.offset ? newItems : [...currentItems, ...newItems];

    this.totalCount.set(response.isLastPage ? combinedItems.length : combinedItems.length + 1);
    this.products.set(combinedItems);
  }
}
